import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { toast } from "sonner";
import { 
  ClipboardList, 
  Plus, 
  Search,
  Check,
  X,
  Trash2,
  AlertTriangle,
  TrendingDown,
  TrendingUp,
  Package,
  FileText,
  Play,
  Square
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Inventory = () => {
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [counts, setCounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchEan, setSearchEan] = useState("");
  const [countedQty, setCountedQty] = useState("");
  const [foundProduct, setFoundProduct] = useState(null);
  const [searching, setSearching] = useState(false);
  const [showNewSession, setShowNewSession] = useState(false);
  const [newSessionName, setNewSessionName] = useState("");
  const [showReport, setShowReport] = useState(false);
  const [reportData, setReportData] = useState(null);

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/inventory/sessions`);
      setSessions(res.data);
      
      // Check for active session
      const active = res.data.find(s => s.status === 'active');
      if (active) {
        setActiveSession(active);
        fetchSessionCounts(active.id);
      }
    } catch (error) {
      console.error("Error fetching sessions:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSessionCounts = async (sessionId) => {
    try {
      const res = await axios.get(`${API}/inventory/sessions/${sessionId}`);
      setCounts(res.data.counts || []);
    } catch (error) {
      console.error("Error fetching counts:", error);
    }
  };

  const createSession = async () => {
    try {
      const res = await axios.post(`${API}/inventory/sessions`, {
        name: newSessionName || undefined
      });
      toast.success("Sessão criada!", {
        description: res.data.session.name
      });
      setShowNewSession(false);
      setNewSessionName("");
      fetchSessions();
    } catch (error) {
      toast.error("Erro ao criar sessão", {
        description: error.response?.data?.detail || "Tente novamente"
      });
    }
  };

  const searchProduct = async () => {
    if (!searchEan.trim()) return;
    
    setSearching(true);
    setFoundProduct(null);
    
    try {
      const res = await axios.get(`${API}/products/search?q=${searchEan}`);
      if (res.data && res.data.length > 0) {
        setFoundProduct(res.data[0]);
        // Check if already counted
        const existing = counts.find(c => c.ean === res.data[0].ean);
        if (existing) {
          setCountedQty(existing.counted_quantity.toString());
        } else {
          setCountedQty("");
        }
      } else {
        toast.error("Produto não encontrado");
      }
    } catch (error) {
      toast.error("Erro na busca");
    } finally {
      setSearching(false);
    }
  };

  const addCount = async () => {
    if (!foundProduct || !activeSession) return;
    
    try {
      const res = await axios.post(`${API}/inventory/sessions/${activeSession.id}/count`, {
        ean: foundProduct.ean,
        counted_quantity: parseInt(countedQty) || 0
      });
      
      toast.success(res.data.message, {
        description: `${res.data.product_name}: ${res.data.counted} un (diferença: ${res.data.difference >= 0 ? '+' : ''}${res.data.difference})`
      });
      
      // Refresh counts
      fetchSessionCounts(activeSession.id);
      
      // Clear form
      setSearchEan("");
      setCountedQty("");
      setFoundProduct(null);
    } catch (error) {
      toast.error("Erro ao registrar contagem", {
        description: error.response?.data?.detail || "Tente novamente"
      });
    }
  };

  const finishSession = async () => {
    if (!activeSession) return;
    
    if (!window.confirm("Finalizar sessão de inventário?\n\nIsso irá atualizar o estoque de todos os produtos contados.")) {
      return;
    }
    
    try {
      const res = await axios.post(`${API}/inventory/sessions/${activeSession.id}/finish`, {
        apply_adjustments: true
      });
      
      toast.success("Inventário finalizado!", {
        description: `${res.data.products_counted} produtos atualizados. Perda: R$ ${res.data.total_loss_value.toFixed(2)}`
      });
      
      setActiveSession(null);
      setCounts([]);
      fetchSessions();
    } catch (error) {
      toast.error("Erro ao finalizar", {
        description: error.response?.data?.detail || "Tente novamente"
      });
    }
  };

  const viewReport = async (sessionId) => {
    try {
      const res = await axios.get(`${API}/inventory/sessions/${sessionId}/report`);
      setReportData(res.data);
      setShowReport(true);
    } catch (error) {
      toast.error("Erro ao carregar relatório");
    }
  };

  const deleteSession = async (sessionId) => {
    if (!window.confirm("Excluir esta sessão de inventário?")) return;
    
    try {
      await axios.delete(`${API}/inventory/sessions/${sessionId}`);
      toast.success("Sessão excluída");
      if (activeSession?.id === sessionId) {
        setActiveSession(null);
        setCounts([]);
      }
      fetchSessions();
    } catch (error) {
      toast.error("Erro ao excluir");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString('pt-BR');
  };

  // Summary stats for active session
  const sessionStats = {
    totalCounted: counts.length,
    totalLoss: counts.filter(c => c.difference < 0).reduce((sum, c) => sum + Math.abs(c.loss_value || 0), 0),
    totalGain: counts.filter(c => c.difference > 0).reduce((sum, c) => sum + (c.gain_value || 0), 0),
    itemsWithLoss: counts.filter(c => c.difference < 0).length,
    itemsWithGain: counts.filter(c => c.difference > 0).length,
    itemsMatch: counts.filter(c => c.difference === 0).length
  };

  return (
    <div className="p-8 animate-fadeIn" data-testid="inventory-page">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Inventário</h1>
          <p className="text-muted-foreground">Controle e ajuste de estoque físico</p>
        </div>
        {!activeSession && (
          <Button onClick={() => setShowNewSession(true)} data-testid="new-session-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nova Sessão
          </Button>
        )}
      </div>

      {/* Active Session */}
      {activeSession && (
        <>
          {/* Session Header */}
          <Card className="mb-6 border-green-200 bg-green-50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                  <div>
                    <h2 className="font-semibold text-lg">{activeSession.name}</h2>
                    <p className="text-sm text-muted-foreground">
                      Iniciado em {formatDate(activeSession.started_at)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => deleteSession(activeSession.id)}>
                    <Trash2 className="w-4 h-4 mr-1" />
                    Cancelar
                  </Button>
                  <Button onClick={finishSession} className="bg-green-600 hover:bg-green-700">
                    <Check className="w-4 h-4 mr-2" />
                    Finalizar Inventário
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <Card>
              <CardContent className="pt-4 pb-4">
                <p className="text-xs text-muted-foreground">Contados</p>
                <p className="text-2xl font-bold">{sessionStats.totalCounted}</p>
              </CardContent>
            </Card>
            <Card className="bg-red-50 border-red-200">
              <CardContent className="pt-4 pb-4">
                <p className="text-xs text-red-700">Perdas</p>
                <p className="text-2xl font-bold text-red-700">{formatCurrency(sessionStats.totalLoss)}</p>
                <p className="text-xs text-red-600">{sessionStats.itemsWithLoss} itens</p>
              </CardContent>
            </Card>
            <Card className="bg-green-50 border-green-200">
              <CardContent className="pt-4 pb-4">
                <p className="text-xs text-green-700">Sobras</p>
                <p className="text-2xl font-bold text-green-700">{formatCurrency(sessionStats.totalGain)}</p>
                <p className="text-xs text-green-600">{sessionStats.itemsWithGain} itens</p>
              </CardContent>
            </Card>
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="pt-4 pb-4">
                <p className="text-xs text-blue-700">Conferidos OK</p>
                <p className="text-2xl font-bold text-blue-700">{sessionStats.itemsMatch}</p>
              </CardContent>
            </Card>
            <Card className={sessionStats.totalLoss > sessionStats.totalGain ? "bg-red-100 border-red-300" : "bg-green-100 border-green-300"}>
              <CardContent className="pt-4 pb-4">
                <p className="text-xs text-muted-foreground">Resultado</p>
                <p className={`text-2xl font-bold ${sessionStats.totalLoss > sessionStats.totalGain ? 'text-red-700' : 'text-green-700'}`}>
                  {formatCurrency(sessionStats.totalGain - sessionStats.totalLoss)}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Count Form */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Search className="w-5 h-5" />
                Registrar Contagem
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4 items-end">
                <div className="flex-1">
                  <label className="text-sm font-medium mb-1 block">EAN ou Nome do Produto</label>
                  <Input
                    value={searchEan}
                    onChange={(e) => setSearchEan(e.target.value)}
                    placeholder="Digite o EAN ou nome..."
                    onKeyDown={(e) => e.key === 'Enter' && searchProduct()}
                    data-testid="search-ean-input"
                  />
                </div>
                <Button onClick={searchProduct} disabled={searching} variant="outline">
                  <Search className="w-4 h-4 mr-2" />
                  Buscar
                </Button>
              </div>

              {foundProduct && (
                <div className="mt-4 p-4 bg-muted/50 rounded-lg">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="font-semibold">{foundProduct.name}</p>
                      <p className="text-sm text-muted-foreground font-mono">EAN: {foundProduct.ean}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">Estoque Sistema</p>
                      <p className="text-2xl font-bold">{foundProduct.quantity}</p>
                    </div>
                  </div>
                  <div className="flex gap-4 items-end">
                    <div className="flex-1">
                      <label className="text-sm font-medium mb-1 block">Quantidade Contada</label>
                      <Input
                        type="number"
                        value={countedQty}
                        onChange={(e) => setCountedQty(e.target.value)}
                        placeholder="0"
                        className="text-2xl font-mono h-14"
                        data-testid="counted-qty-input"
                      />
                    </div>
                    <Button onClick={addCount} className="h-14 px-8" data-testid="add-count-btn">
                      <Check className="w-5 h-5 mr-2" />
                      Registrar
                    </Button>
                  </div>
                  {countedQty && (
                    <div className="mt-3 text-center">
                      <span className={`text-lg font-semibold ${
                        parseInt(countedQty) < foundProduct.quantity ? 'text-red-600' :
                        parseInt(countedQty) > foundProduct.quantity ? 'text-green-600' : 'text-blue-600'
                      }`}>
                        Diferença: {parseInt(countedQty) - foundProduct.quantity >= 0 ? '+' : ''}{parseInt(countedQty) - foundProduct.quantity}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Counts List */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <ClipboardList className="w-5 h-5" />
                Produtos Contados ({counts.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {counts.length > 0 ? (
                <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                  <table className="data-table text-sm">
                    <thead className="sticky top-0 bg-white">
                      <tr>
                        <th>EAN</th>
                        <th>Produto</th>
                        <th>Sistema</th>
                        <th>Contado</th>
                        <th>Diferença</th>
                        <th>Valor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {counts.map((count) => (
                        <tr key={count.ean} className={
                          count.difference < 0 ? 'bg-red-50' :
                          count.difference > 0 ? 'bg-green-50' : ''
                        }>
                          <td className="font-mono text-xs">{count.ean}</td>
                          <td className="max-w-[200px] truncate">{count.product_name}</td>
                          <td className="font-mono text-center">{count.expected_quantity}</td>
                          <td className="font-mono text-center font-bold">{count.counted_quantity}</td>
                          <td className={`font-mono text-center font-bold ${
                            count.difference < 0 ? 'text-red-600' :
                            count.difference > 0 ? 'text-green-600' : 'text-blue-600'
                          }`}>
                            {count.difference >= 0 ? '+' : ''}{count.difference}
                          </td>
                          <td className={`font-mono ${count.difference < 0 ? 'text-red-600' : count.difference > 0 ? 'text-green-600' : ''}`}>
                            {count.difference < 0 ? `-${formatCurrency(count.loss_value)}` :
                             count.difference > 0 ? `+${formatCurrency(count.gain_value || 0)}` : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="py-8 text-center text-muted-foreground">
                  <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Nenhum produto contado ainda</p>
                  <p className="text-sm">Use a busca acima para adicionar contagens</p>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Sessions List (when no active session) */}
      {!activeSession && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Histórico de Inventários
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="py-8 text-center text-muted-foreground">Carregando...</div>
            ) : sessions.length > 0 ? (
              <div className="space-y-3">
                {sessions.map((session) => (
                  <div 
                    key={session.id} 
                    className={`p-4 rounded-lg border ${
                      session.status === 'active' ? 'border-green-300 bg-green-50' : 'border-muted'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold">{session.name}</h3>
                          {session.status === 'active' ? (
                            <Badge className="bg-green-100 text-green-700">Ativa</Badge>
                          ) : (
                            <Badge variant="secondary">Finalizada</Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          {session.status === 'completed' 
                            ? `Finalizado em ${formatDate(session.completed_at)}`
                            : `Iniciado em ${formatDate(session.started_at)}`
                          }
                        </p>
                        {session.status === 'completed' && (
                          <div className="flex gap-4 mt-2 text-sm">
                            <span>{session.total_products_counted} produtos</span>
                            <span className="text-red-600">Perda: {formatCurrency(session.total_loss_value)}</span>
                            <span className="text-green-600">Sobra: {formatCurrency(session.total_gain_value)}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2">
                        {session.status === 'completed' && (
                          <Button variant="outline" size="sm" onClick={() => viewReport(session.id)}>
                            <FileText className="w-4 h-4 mr-1" />
                            Relatório
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => deleteSession(session.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-8 text-center text-muted-foreground">
                <ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Nenhum inventário realizado</p>
                <p className="text-sm">Clique em "Nova Sessão" para começar</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* New Session Dialog */}
      <Dialog open={showNewSession} onOpenChange={setShowNewSession}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nova Sessão de Inventário</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium mb-1 block">Nome da Sessão (opcional)</label>
            <Input
              value={newSessionName}
              onChange={(e) => setNewSessionName(e.target.value)}
              placeholder={`Inventário ${new Date().toLocaleDateString('pt-BR')}`}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewSession(false)}>Cancelar</Button>
            <Button onClick={createSession}>
              <Play className="w-4 h-4 mr-2" />
              Iniciar Inventário
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Report Dialog */}
      <Dialog open={showReport} onOpenChange={setShowReport}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Relatório de Inventário</DialogTitle>
          </DialogHeader>
          
          {reportData && (
            <div className="flex-1 overflow-auto">
              {/* Summary */}
              <div className="grid grid-cols-4 gap-3 mb-4">
                <div className="bg-muted/50 rounded p-3">
                  <p className="text-xs text-muted-foreground">Produtos Contados</p>
                  <p className="text-xl font-bold">{reportData.summary.total_counted}</p>
                </div>
                <div className="bg-red-50 rounded p-3">
                  <p className="text-xs text-red-700">Total Perdas</p>
                  <p className="text-xl font-bold text-red-700">{formatCurrency(reportData.summary.loss_value)}</p>
                  <p className="text-xs">{reportData.summary.total_losses} itens</p>
                </div>
                <div className="bg-green-50 rounded p-3">
                  <p className="text-xs text-green-700">Total Sobras</p>
                  <p className="text-xl font-bold text-green-700">{formatCurrency(reportData.summary.gain_value)}</p>
                  <p className="text-xs">{reportData.summary.total_gains} itens</p>
                </div>
                <div className="bg-blue-50 rounded p-3">
                  <p className="text-xs text-blue-700">Conferidos OK</p>
                  <p className="text-xl font-bold text-blue-700">{reportData.summary.total_matches}</p>
                </div>
              </div>

              <Tabs defaultValue="losses">
                <TabsList>
                  <TabsTrigger value="losses" className="text-red-600">
                    <TrendingDown className="w-4 h-4 mr-1" />
                    Perdas ({reportData.losses.length})
                  </TabsTrigger>
                  <TabsTrigger value="gains" className="text-green-600">
                    <TrendingUp className="w-4 h-4 mr-1" />
                    Sobras ({reportData.gains.length})
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="losses" className="mt-4">
                  {reportData.losses.length > 0 ? (
                    <table className="data-table text-sm">
                      <thead>
                        <tr>
                          <th>EAN</th>
                          <th>Produto</th>
                          <th>Sistema</th>
                          <th>Contado</th>
                          <th>Diferença</th>
                          <th>Valor Perda</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.losses.map((item) => (
                          <tr key={item.ean} className="bg-red-50">
                            <td className="font-mono text-xs">{item.ean}</td>
                            <td>{item.product_name}</td>
                            <td className="font-mono text-center">{item.expected_quantity}</td>
                            <td className="font-mono text-center">{item.counted_quantity}</td>
                            <td className="font-mono text-center text-red-600 font-bold">{item.difference}</td>
                            <td className="font-mono text-red-600 font-bold">{formatCurrency(item.loss_value)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="text-center py-4 text-muted-foreground">Nenhuma perda registrada</p>
                  )}
                </TabsContent>

                <TabsContent value="gains" className="mt-4">
                  {reportData.gains.length > 0 ? (
                    <table className="data-table text-sm">
                      <thead>
                        <tr>
                          <th>EAN</th>
                          <th>Produto</th>
                          <th>Sistema</th>
                          <th>Contado</th>
                          <th>Diferença</th>
                          <th>Valor Sobra</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.gains.map((item) => (
                          <tr key={item.ean} className="bg-green-50">
                            <td className="font-mono text-xs">{item.ean}</td>
                            <td>{item.product_name}</td>
                            <td className="font-mono text-center">{item.expected_quantity}</td>
                            <td className="font-mono text-center">{item.counted_quantity}</td>
                            <td className="font-mono text-center text-green-600 font-bold">+{item.difference}</td>
                            <td className="font-mono text-green-600 font-bold">{formatCurrency(item.gain_value || 0)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="text-center py-4 text-muted-foreground">Nenhuma sobra registrada</p>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          )}

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowReport(false)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Inventory;
